import React, {useEffect, useState} from 'react';
import * as Yup from 'yup';
import {compose} from 'redux';
import {connect} from 'react-redux';

import './styles.scss';
import ModalTemplate from '../../../../../modal';
import {
  AppForm,
  AppFormDatePickerField,
  AppFormSelectField,
  AppFormField,
  AppFormSubmitButton,
} from '../../../../../forms';
import {Column, Row} from '../../../../../layout';
import {
  has18Years,
  today,
  getDateInServerFormat,
} from '../../../../../../util/utility';
import * as fonts from '../../../../../../util/fonts';
import {
  addToGlobalStyles,
  addToUser,
  changeUserDOB,
  setAvailableBus,
  setBusFuncMap,
  setBusMap,
  setBusStylesMap,
  setSelectedBu,
  setUser,
} from '../../../../../../store/actions';
import {get} from '../../../../../../api/api';
import {
  uriCard,
  uriGetBus,
  uriGetNewCardNewData,
} from '../../../../../../api/endpoints';
import Icon from '../../../../../icon';
import {Icons} from '../../../../../../util/icons';
import colors from '../../../../../../util/colors';
import AppButton from '../../../../../app-button';
import {strings} from '../../../../../../util/strings';
import {useHistory} from 'react-router-dom';

interface InsertNewDataModalProps {
  show: boolean;
  setShow(value: boolean): void;
  user: any;
  labels: typeof strings.pt.virtualCard;
  availableBus: any;
  setBusStylesMap(value: any): void;
  setAvailableBus(value: any): void;
  setBusMap(value: any): void;
  setSelectedBu(value: any): void;
  addToUser(value: any): void;
  changeUserDOB(value: any): void;
  setUser(value: any): void;
  busStyles: any;
  busFunctionalities: any;
}

const validationSchema = Yup.object().shape({
  dob: Yup.date().required().max(today(true)),
  buId: Yup.string().required(),
  cardNumber: Yup.string().required().max(20),
});

function InsertNewDataModal({
  show,
  setShow,
  user,
  labels,
  availableBus,
  setBusStylesMap,
  setAvailableBus,
  setBusMap,
  setSelectedBu,
  addToUser,
  changeUserDOB,
  setUser,
  busStyles,
  busFunctionalities,
}: InsertNewDataModalProps) {
  const [valid, setValid] = useState(false);
  const [minorUser, setMinorUser] = useState(false);
  const [dataNotFound, setDataNotFound] = useState(false);
  const [generalError, setGeneralError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [buOptions, setBuOptions] = useState<any[]>([]);
  const [dob, setDob] = useState<string>('');
  const history = useHistory();
  const featureName = 'VirtualCard-InsertData';

  useEffect(() => {
    const options = Object.keys(availableBus).map((elem: any) => {
      const bu = availableBus[elem];
      return {
        label: bu.name,
        value: bu.id,
      };
    });
    setBuOptions(options);
  }, [availableBus]);

  const onSubmit = async (values: any) => {
    setSubmitting(true);
    // if the user has at least 18 years continues
    if (has18Years(values.dob)) {
      // if the user inserts valid data the same logic as in the login page will be applyed
      try {
        // getting user bus
        const userBusResponse: any = await get(
          uriGetNewCardNewData(
            user.nif,
            getDateInServerFormat(values.dob),
            values.buId,
            values.cardNumber
          )
        );

        const busResponse: any = await get(uriGetBus());
        const bus = busResponse?.data?.items;
        const busMap: any = {};

        // creating a new map with the bus and cards from the response
        userBusResponse.forEach((elem: any) => {
          const famId = elem.famId;
          const buId = elem.buId;
          const existingBu = !!busMap[buId];

          const member = {...elem};
          const memberBu = bus.find((bu: any) => bu.id === buId);
          member.buName = (memberBu && memberBu.name) || '';

          if (existingBu) {
            // if the bu already exists in the map: checks for existing groups in map
            const existingGroup = !!busMap[buId][famId];

            if (existingGroup) {
              // if the group already exists in the map: adds the current member to the preexisting group
              busMap[buId][famId].push(member);
            } else {
              // otherwise: adds the new group to the map prefilled with the current member
              busMap[buId][famId] = [member];
            }
          } else {
            // otherwise: adds the new bu prefilled with the current member's group
            busMap[buId] = {[`${famId}`]: [member]};
          }
        });

        // for each of the users's bu adds a new key to the bus styles map with the correnponding bu style returned from the BO calls
        let busStylesMap: any = {};
        Object.keys(busMap).forEach(
          (key: string, index: number) => (busStylesMap[key] = busStyles[index])
        );
        setBusStylesMap(busStylesMap);

        // for each of the users's bu adds a new key to the bus func map with the correnponding bu functionalities returned from the BO calls
        let busFuncMap: any = {};
        Object.keys(busMap).forEach(
          (key: string, index: number) =>
            (busFuncMap[key] = busFunctionalities[index].funcionalidade)
        );
        setBusFuncMap(busFuncMap);

        // buId for the user's first bu

        // for each card/member of each user's bu obtains the card data and adds it to the busMap
        await Promise.all(
          Object.keys(busMap).flatMap((buId) => {
            return Object.keys(busMap[buId]).map((famId) => {
              return Promise.all(
                busMap[buId][famId].map((member: any, index: number) => {
                  return get(
                    uriCard(member.buId, member.memId, member.famId),
                    false,
                    0,
                    featureName
                  )
                    .then((buUserCard: any) => {
                      // const buUserIndex = busMap[buId][
                      //   famId
                      // ].indexOf(member);
                      busMap[buId][famId][index].card = buUserCard?.data;
                    })
                    .catch((e: any) => {
                      const buUserIndex = busMap[buId][famId].indexOf(member);

                      // if the card api call fails or returns nothing sets the card as {}
                      busMap[buId][famId][buUserIndex].card = {};
                    });
                })
              );
            });
          })
        );

        const auxBus = Object.values(
          Object.values(Object.values(busMap))
        ).flatMap((group: any) => Object.values(group).flatMap((elem) => elem));

        // sets the card/member with selectedProduct as true as the selected card/member/bu in redux
        const newSelectedBu: any =
          auxBus.find((elem: any) => elem.selectedProduct) || auxBus[0];

        setBusMap(busMap);

        // saves all info to redux
        setAvailableBus(userBusResponse);
        updateDobRedux();
        setSelectedBu(newSelectedBu);

        addToUser({
          state: 'CLIENT',
        });
        history.push('/callback');
      } catch (e: any) {
        setDataNotFound(true);
        setDataNotFound(true);
        if (e.status === 404) {
          const newUser = {
            ...user,
            hasMemId: true,
            hasNewData: true,
          };
          setUser(newUser);
        } else {
          setGeneralError(true);
        }
      } finally {
        setSubmitting(false);
      }
    } //otherwise, displays the minor user error message
    else {
      setMinorUser(true);
      setSubmitting(false);
    }
  };

  const updateDobRedux = () => {
    if (dob) {
      var dobDate = new Date(dob);
      var day = dobDate?.getDate();
      var month = dobDate?.getMonth() + 1;
      var year = dobDate?.getFullYear();

      var dobString =
        year +
        '-' +
        (month < 10 ? '0' + month : month) +
        '-' +
        (day < 10 ? '0' + day : day);

      changeUserDOB(dobString);
    }
  };

  return (
    <ModalTemplate
      show={show}
      cross={true}
      onHide={() => {
        setShow(false);
        setMinorUser(false);
        setDataNotFound(false);
      }}
      hideFooter={true}
      centered={false}>
      {minorUser ? (
        <Column>
          <Row className="insert-new-data-modal-row insert-new-data-modal-title insert-new-data-modal-text">
            {labels?.changeInsuranceData_title}
          </Row>

          <Row className="insert-new-data-modal-icon-row">
            <Icon
              changeViewbox={false}
              width="32"
              height="32"
              color={colors.alertRed}
              name={Icons.ALERT_PROBLEM}
            />
          </Row>
          <Row
            className="insert-new-data-modal-row"
            style={{...fonts.MM, color: colors.alertRed}}>
            {labels?.changeInsuranceData_ageError_title}
          </Row>
          <Row
            className="insert-new-data-modal-row"
            style={{
              fontSize: '17px',
              textAlign: 'center',
              marginBottom: '3rem',
            }}>
            {labels?.changeInsuranceData_ageError_text}
          </Row>
          <AppButton
            className="insert-new-data-modal-error-button"
            variant="secondary"
            onClick={() => {
              setShow(false);
              setMinorUser(false);
            }}>
            {labels?.changeInsuranceData_ageError_button_web}
          </AppButton>
        </Column>
      ) : dataNotFound ? (
        <Column>
          <Row className="insert-new-data-modal-row insert-new-data-modal-title insert-new-data-modal-text">
            {labels?.changeInsuranceData_title}
          </Row>

          <Row className="insert-new-data-modal-icon-row">
            <Icon
              changeViewbox={false}
              width="32"
              height="32"
              color={colors.alertRed}
              name={Icons.ALERT_PROBLEM}
            />
          </Row>
          {!generalError && (
            <Row
              className="insert-new-data-modal-row"
              style={{...fonts.MM, color: colors.alertRed}}>
              {labels?.changeInsuranceData_notFoundError_title}
            </Row>
          )}
          {generalError && (
            <Row
              className="insert-new-data-modal-row"
              style={{...fonts.MM, color: colors.alertRed}}>
              {labels?.changeInsuranceData_generalErrorOcurred}
            </Row>
          )}

          {!generalError && (
            <Row className="insert-new-data-modal-row">
              {labels?.changeInsuranceData_notFoundError_text}
            </Row>
          )}
          {generalError && (
            <Row className="insert-new-data-modal-row">
              {labels?.changeInsuranceData_generalErrorTryAgain}
            </Row>
          )}

          <AppButton
            className="insert-new-data-modal-error-button"
            variant="secondary"
            onClick={() => {
              setShow(false);
              setDataNotFound(false);
              setGeneralError(false);
            }}>
            {labels?.changeInsuranceData_notFoundError_button_web}
          </AppButton>
        </Column>
      ) : (
        <Column>
          <Row className="insert-new-data-modal-row insert-new-data-modal-title insert-new-data-modal-title-text">
            {labels?.changeInsuranceData_title}
          </Row>
          <Row
            className="insert-new-data-modal-row insert-new-data-modal-text"
            style={{...fonts.M}}>
            {labels?.changeInsuranceData_subTitle}
          </Row>
          <Row
            className="insert-new-data-modal-row insert-new-data-modal-text insert-new-data-modal-subtitle-row"
            style={{...fonts.M}}>
            {labels?.changeInsuranceData_subTitleBottom}
          </Row>
          <AppForm
            initialValues={{
              dob: '',
              buId: '',
              cardNumber: '',
            }}
            validationSchema={validationSchema}
            onSubmit={(values) => onSubmit(values)}
            onValidation={(valid) => setValid(valid)}
            onChange={(values) => setDob(values.dob)}>
            <Row className="insert-new-data-modal-row insert-new-data-modal-field-row">
              <AppFormDatePickerField
                name="dob"
                placeholder={labels?.changeInsuranceData_birthDatePlacehoder}
              />
            </Row>
            <Row className="insert-new-data-modal-row insert-new-data-modal-field-row insert-new-data-modal-select-row">
              <AppFormSelectField
                name="buId"
                placeholder={labels?.changeInsuranceData_insurancePlaceholder}
                options={buOptions}
              />
            </Row>
            <Row className="insert-new-data-modal-row insert-new-data-modal-field-row">
              <AppFormField
                name="cardNumber"
                placeholder={
                  labels?.changeInsuranceData_insuranceNumberPlaceholder
                }
              />
            </Row>

            <AppFormSubmitButton
              title={labels?.changeInsuranceData_button}
              disabled={!valid || submitting}
              className="insert-new-data-modal-submit-button"
            />
          </AppForm>
        </Column>
      )}
    </ModalTemplate>
  );
}

const mapStateToProps = ({
  user,
  strings,
  availableBus,
  busStyles,
  busFunctionalities,
}: any) => ({
  user: user.value,
  labels: strings.value.pt.virtualCard,
  availableBus: availableBus.value,
  busStyles: busStyles.value,
  busFunctionalities: busFunctionalities.value,
});

const mapDispatchToProps = (dispatch: any) => ({
  setBusStylesMap: (value: any) => dispatch(setBusStylesMap(value)),
  setAvailableBus: (value: any) => dispatch(setAvailableBus(value)),
  setBusMap: (value: any) => dispatch(setBusMap(value)),
  setSelectedBu: (value: any) => dispatch(setSelectedBu(value)),
  addToUser: (value: any) => dispatch(addToUser(value)),
  setBusFuncMap: (value: any) => dispatch(setBusFuncMap(value)),
  addToGlobalStyles: (value: any) => dispatch(addToGlobalStyles(value)),
  changeUserDOB: (value: any) => dispatch(changeUserDOB(value)),
  setUser: (value: any) => dispatch(setUser(value)),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps)(InsertNewDataModal)
);
